// ═══════════════════════════════════════════════════════════════════════════
// roles.js — Client-side accessors for the user_roles table (migration 059).
//
// Single source of truth for "what can this signed-in user do in the CMS?":
// - getMyRole()                     — current user's role + scopes
// - listRoleRows()                  — every role row (admin Team page)
// - findUserByEmail(email)          — invite-by-email lookup
// - assignRole(userId, role, ...)   — grant or update a role
// - removeRole(userId)              — revoke
//
// Every write here goes through Supabase RLS, which enforces "only
// admins write" — these helpers do NOT re-check on the client because a
// determined caller could just bypass the helper. The DB is the
// guardrail. The client checks here are UX (hide buttons the user
// can't use) and concurrency (avoid optimistic-grant flicker), not
// security.
// ═══════════════════════════════════════════════════════════════════════════

import { supabase } from './supabase';

// Canonical scope strings. Adding a new scope = add to this list +
// add the matching guard at the AdminPage tab. UI uses these to render
// the scope-picker checkboxes.
export const SCOPES = [
  {
    id:    'users',
    label: 'Users',
    description: 'See the user list, view profiles, run the verification queue.',
  },
  {
    id:    'support',
    label: 'Support',
    description: 'Read + reply to support tickets.',
  },
  {
    id:    'content',
    label: 'Content',
    description: 'Manage Explore shelves, featured creators, home banners.',
  },
  {
    id:    'moderation',
    label: 'Moderation',
    description: 'Review the content-report queue, remove content, action accounts.',
  },
];

export const SCOPE_IDS = SCOPES.map(s => s.id);

// ── Read ───────────────────────────────────────────────────────────────────

/**
 * Returns the current user's role row, or null if they don't have one.
 * Wraps the my_role() RPC (security-definer, returns the caller's
 * single row).
 */
export async function getMyRole() {
  if (!supabase) return null;
  try {
    const { data, error } = await supabase.rpc('my_role');
    if (error) {
      // Table not migrated yet → return null instead of throwing.
      // The app degrades to "no role" cleanly during the migration
      // window before the SQL is applied.
      if (isMissingObjectError(error)) return null;
      console.warn('getMyRole error:', error);
      return null;
    }
    const row = Array.isArray(data) ? data[0] : data;
    if (!row) return null;
    return {
      role:   row.role,
      scopes: Array.isArray(row.scopes) ? row.scopes : [],
    };
  } catch (e) {
    console.warn('getMyRole threw:', e);
    return null;
  }
}

/**
 * Lists every user_roles row + email/name/avatar for the Team page.
 * RPC enforces "admin only" — non-admins get an empty array.
 */
export async function listRoleRows() {
  if (!supabase) return [];
  const { data, error } = await supabase.rpc('list_role_rows');
  if (error) {
    if (isMissingObjectError(error)) return [];
    console.error('listRoleRows error:', error);
    return [];
  }
  return data || [];
}

/**
 * Looks up an existing user by email. Used by the Team page invite
 * flow: admin types an email, we resolve it to a user_id (or surface
 * "not on Epio yet" if no match). Returns the matched row or null.
 */
export async function findUserByEmail(email) {
  if (!supabase || !email) return null;
  const clean = String(email).trim().toLowerCase();
  if (!clean) return null;
  const { data, error } = await supabase.rpc('find_user_by_email', { p_email: clean });
  if (error) {
    if (isMissingObjectError(error)) return null;
    console.error('findUserByEmail error:', error);
    return null;
  }
  return Array.isArray(data) ? (data[0] || null) : (data || null);
}

// ── Write (admin only via RLS) ─────────────────────────────────────────────

/**
 * Grant or update a role for a user. Validates the role + scopes
 * client-side (UX), but the DB CHECK + RLS are the real guards.
 *   role:   'admin' | 'manager'
 *   scopes: string[] — must be a subset of SCOPE_IDS. Ignored when
 *           role='admin' (admins implicitly have every scope).
 */
export async function assignRole(userId, role, scopes = []) {
  if (!supabase || !userId) throw new Error('Missing user');
  if (!['admin', 'manager'].includes(role)) {
    throw new Error(`Invalid role: ${role}`);
  }
  const cleanScopes = role === 'admin'
    ? []   // admins implicitly cover every scope; stored as empty
    : Array.from(new Set((scopes || []).filter(s => SCOPE_IDS.includes(s))));

  const granter = (await supabase.auth.getUser())?.data?.user?.id || null;

  const { data, error } = await supabase
    .from('user_roles')
    .upsert({
      user_id:    userId,
      role,
      scopes:     cleanScopes,
      granted_by: granter,
    }, { onConflict: 'user_id' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

/**
 * Revoke any role + scopes on a user. Hard delete — the row is
 * audit-trailed via Supabase's database logs, not via a soft-delete
 * column. If we ever need an audit log, add a separate user_role_events
 * table; don't repurpose this row.
 */
export async function removeRole(userId) {
  if (!supabase || !userId) throw new Error('Missing user');
  const { error } = await supabase
    .from('user_roles')
    .delete()
    .eq('user_id', userId);
  if (error) throw error;
  return true;
}

// ── Helpers ────────────────────────────────────────────────────────────────

/**
 * True when a role + scopes payload covers the requested scope.
 * Admins implicitly have every scope. Managers must have it in
 * their scopes array. Non-roles never have any scope.
 */
export function hasScope(roleRow, scope) {
  if (!roleRow) return false;
  if (roleRow.role === 'admin') return true;
  if (roleRow.role === 'manager') {
    return Array.isArray(roleRow.scopes) && roleRow.scopes.includes(scope);
  }
  return false;
}

/**
 * Migration-tolerant error check — when 059 hasn't been applied yet,
 * Supabase returns a "could not find …" / "schema cache" error. We
 * treat that as "no role" rather than blowing up the app.
 */
function isMissingObjectError(err) {
  if (!err) return false;
  const msg = (err.message || '').toLowerCase();
  return (
    msg.includes('schema cache') ||
    msg.includes('does not exist') ||
    msg.includes('could not find') ||
    err.code === 'PGRST202' ||  // function not found
    err.code === 'PGRST205' ||  // table not found
    err.code === '42P01'        // undefined table
  );
}
